import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo, useLayoutEffect } from 'react';
import ConfigurationService from '../services/ConfigurationService';
import { useThemeStore } from '../stores/useThemeStore';
import { CONFIG_EVENTS, UPDATE_KEYS, TYPOGRAPHIC_SCALE, TYPOGRAPHY_DEFAULTS } from '../constants';
import { mapTokenToCss } from '../utils/uiHelpers';
import type { ThemeConfiguration, TokenMappings, TableConfiguration, ThemeModeColors } from '../types/configuration.types';

interface ThemeContextValue {
  theme: ThemeConfiguration;
  isDark: boolean;
  toggleDarkMode: () => void;
  refreshTheme: () => Promise<void>;
  tokenMappings: TokenMappings | undefined;
  resolvedTable: TableConfiguration;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider Component
 *
 * Manages dynamic theme configuration loaded from backend via ConfigurationService.
 * Applies CSS variables to document root for global styling.
 * Supports dark/light mode toggle and live theme updates via WebSocket.
 *
 * **Features:**
 * - Fetches theme from ConfigurationService (which loads from backend)
 * - Loads theme on mount from ConfigurationService
 * - Listens for CONFIG_EVENTS.UPDATED WebSocket events for live theme updates
 * - Applies CSS variables to document root for global styling
 * - Provides theme context to all child components
 * - UNIFIED: Subscribes to useThemeStore for mode state
 */
/**
 * Merges a potentially partial ThemeModeColors object with a fallback ThemeModeColors object.
 * This ensures all keys needed by the CSS injection are present.
 */
function mergeModeConfig(
  mode: Partial<ThemeModeColors> | undefined | null,
  fallback: ThemeModeColors
): ThemeModeColors {
  if (!mode) {
    return fallback;
  }

  return {
    bgPrimary: mode.bgPrimary || fallback.bgPrimary,
    bgSecondary: mode.bgSecondary || fallback.bgSecondary,
    bgSurface: mode.bgSurface || fallback.bgSurface,
    textPrimary: mode.textPrimary || fallback.textPrimary,
    textSecondary: mode.textSecondary || fallback.textSecondary,
    sidebarText: mode.sidebarText || fallback.sidebarText,
    sidebarIcon: mode.sidebarIcon || fallback.sidebarIcon,
    divider: mode.divider || fallback.divider,
    dividerWidth: mode.dividerWidth || fallback.dividerWidth,
    dividerOpacity: typeof mode.dividerOpacity === 'number' ? mode.dividerOpacity : fallback.dividerOpacity,
    partition: mode.partition || fallback.partition,
    chartPalette: Array.isArray(mode.chartPalette) && mode.chartPalette.length > 0 ? mode.chartPalette : fallback.chartPalette,
    chartGrid: mode.chartGrid || fallback.chartGrid,
    chartTextColor: mode.chartTextColor || fallback.chartTextColor,
    chartFontSize: typeof mode.chartFontSize === 'number' ? mode.chartFontSize : fallback.chartFontSize,
    chartFontFamily: mode.chartFontFamily || fallback.chartFontFamily,
    chartFontWeight: mode.chartFontWeight !== undefined ? mode.chartFontWeight : fallback.chartFontWeight,
    statsContainerBg: mode.statsContainerBg || fallback.statsContainerBg,
    statsContainerOpacity: typeof mode.statsContainerOpacity === 'number' ? mode.statsContainerOpacity : fallback.statsContainerOpacity,
    dashboardCardBg: mode.dashboardCardBg || fallback.dashboardCardBg,
    dashboardCardOpacity: typeof mode.dashboardCardOpacity === 'number' ? mode.dashboardCardOpacity : fallback.dashboardCardOpacity,
    selection: mode.selection || fallback.selection,
    hoverOverlay: mode.hoverOverlay || fallback.hoverOverlay,
    successSurface: mode.successSurface || fallback.successSurface,
    successAccent: mode.successAccent || fallback.successAccent,
    warningSurface: mode.warningSurface || fallback.warningSurface,
    warningAccent: mode.warningAccent || fallback.warningAccent,
    errorSurface: mode.errorSurface || fallback.errorSurface,
    errorAccent: mode.errorAccent || fallback.errorAccent,
    infoSurface: mode.infoSurface || fallback.infoSurface,
    infoAccent: mode.infoAccent || fallback.infoAccent,
    statusBadgeMix: mode.statusBadgeMix || fallback.statusBadgeMix,
    statusBadgeOpacity: typeof mode.statusBadgeOpacity === 'number' ? mode.statusBadgeOpacity : fallback.statusBadgeOpacity,
    transitionArrowOpacity: typeof mode.transitionArrowOpacity === 'number' ? mode.transitionArrowOpacity : fallback.transitionArrowOpacity,
    transitionArrowWeight: mode.transitionArrowWeight || fallback.transitionArrowWeight,
    cardBg: mode.cardBg || fallback.cardBg,
    tableBg: mode.tableBg || fallback.tableBg,
    tableBgOpacity: typeof mode.tableBgOpacity === 'number' ? mode.tableBgOpacity : fallback.tableBgOpacity,
    tableHeaderBg: mode.tableHeaderBg || fallback.tableHeaderBg,
    tableHeaderBgOpacity: typeof mode.tableHeaderBgOpacity === 'number' ? mode.tableHeaderBgOpacity : fallback.tableHeaderBgOpacity,
    tableHeaderColor: mode.tableHeaderColor || fallback.tableHeaderColor,
    headerText: mode.headerText || fallback.headerText,
    tableBorder: mode.tableBorder || fallback.tableBorder,
    tableBorderOpacity: typeof mode.tableBorderOpacity === 'number' ? mode.tableBorderOpacity : fallback.tableBorderOpacity,
    tableStripeColor1: mode.tableStripeColor1 || fallback.tableStripeColor1,
    tableStripeOpacity1: typeof mode.tableStripeOpacity1 === 'number' ? mode.tableStripeOpacity1 : fallback.tableStripeOpacity1,
    tableStripeColor2: mode.tableStripeColor2 || fallback.tableStripeColor2,
    tableStripeOpacity2: typeof mode.tableStripeOpacity2 === 'number' ? mode.tableStripeOpacity2 : fallback.tableStripeOpacity2,
    tableHoverColor: mode.tableHoverColor || fallback.tableHoverColor,
    tableHoverOpacity: typeof mode.tableHoverOpacity === 'number' ? mode.tableHoverOpacity : fallback.tableHoverOpacity,
    tableSelectedColor: mode.tableSelectedColor || fallback.tableSelectedColor,
    tableSelectedOpacity: typeof mode.tableSelectedOpacity === 'number' ? mode.tableSelectedOpacity : fallback.tableSelectedOpacity,
    elevation: {
      rimLightOpacity: typeof mode.elevation?.rimLightOpacity === 'number' ? mode.elevation.rimLightOpacity : fallback.elevation.rimLightOpacity,
      shadowColor: mode.elevation?.shadowColor || fallback.elevation.shadowColor,
    },
    statusColors: mode.statusColors || fallback.statusColors,
    aesthetics: mode.aesthetics || fallback.aesthetics,
    table: mode.table || fallback.table,
    tokenMappings: mode.tokenMappings || fallback.tokenMappings,
  };
}

/**
 * Validates and sanitizes dynamic theme configuration records.
 * Performs nested structural checks and fills missing property nodes with configuration defaults.
 */
export function validateThemeStructure(themeConfig: ThemeConfiguration | undefined | null): ThemeConfiguration {
  const fallback = ConfigurationService.getFallbackConfig().theme;
  if (!themeConfig) {
    return fallback;
  }

  return {
    primaryColor: themeConfig.primaryColor || fallback.primaryColor,
    secondaryColor: themeConfig.secondaryColor || fallback.secondaryColor,
    accentColor: themeConfig.accentColor || fallback.accentColor,
    borderRadius: themeConfig.borderRadius || fallback.borderRadius,
    statusColors: {
      success: themeConfig.statusColors?.success || fallback.statusColors.success,
      warning: themeConfig.statusColors?.warning || fallback.statusColors.warning,
      error: themeConfig.statusColors?.error || fallback.statusColors.error,
      neutral: themeConfig.statusColors?.neutral || fallback.statusColors.neutral,
      info: themeConfig.statusColors?.info || fallback.statusColors.info,
    },
    darkMode: mergeModeConfig(themeConfig.darkMode, fallback.darkMode),
    lightMode: mergeModeConfig(themeConfig.lightMode, fallback.lightMode),
    typography: {
      fontFamily: themeConfig.typography?.fontFamily || fallback.typography.fontFamily,
      fontSize: {
        base: themeConfig.typography?.fontSize?.base || fallback.typography.fontSize.base,
        sm: themeConfig.typography?.fontSize?.sm || fallback.typography.fontSize.sm,
        lg: themeConfig.typography?.fontSize?.lg || fallback.typography.fontSize.lg,
      },
      lineHeight: themeConfig.typography?.lineHeight || fallback.typography.lineHeight,
      tracking: {
        tight: themeConfig.typography?.tracking?.tight || fallback.typography.tracking.tight,
        normal: themeConfig.typography?.tracking?.normal || fallback.typography.tracking.normal,
        wide: themeConfig.typography?.tracking?.wide || fallback.typography.tracking.wide,
      },
      fontFeatureSettings: themeConfig.typography?.fontFeatureSettings || fallback.typography.fontFeatureSettings,
    },
    aesthetics: {
      glassmorphism: {
        enabled: typeof themeConfig.aesthetics?.glassmorphism?.enabled === 'boolean'
          ? themeConfig.aesthetics.glassmorphism.enabled
          : fallback.aesthetics.glassmorphism.enabled,
        blurIntensity: themeConfig.aesthetics?.glassmorphism?.blurIntensity || fallback.aesthetics.glassmorphism.blurIntensity,
        transparency: typeof themeConfig.aesthetics?.glassmorphism?.transparency === 'number'
          ? themeConfig.aesthetics.glassmorphism.transparency
          : fallback.aesthetics.glassmorphism.transparency,
        noiseAlpha: typeof themeConfig.aesthetics?.glassmorphism?.noiseAlpha === 'number'
          ? themeConfig.aesthetics.glassmorphism.noiseAlpha
          : fallback.aesthetics.glassmorphism.noiseAlpha,
        saturation: typeof themeConfig.aesthetics?.glassmorphism?.saturation === 'number'
          ? themeConfig.aesthetics.glassmorphism.saturation
          : fallback.aesthetics.glassmorphism.saturation,
      },
      motion: {
        speed: themeConfig.aesthetics?.motion?.speed || fallback.aesthetics.motion.speed,
        easingCurve: themeConfig.aesthetics?.motion?.easingCurve || fallback.aesthetics.motion.easingCurve,
        staggerDelayMs: typeof themeConfig.aesthetics?.motion?.staggerDelayMs === 'number'
          ? themeConfig.aesthetics.motion.staggerDelayMs
          : fallback.aesthetics.motion.staggerDelayMs,
        interactiveScale: typeof themeConfig.aesthetics?.motion?.interactiveScale === 'boolean'
          ? themeConfig.aesthetics.motion.interactiveScale
          : fallback.aesthetics.motion.interactiveScale,
        pageTransitionEnabled: typeof themeConfig.aesthetics?.motion?.pageTransitionEnabled === 'boolean'
          ? themeConfig.aesthetics.motion.pageTransitionEnabled
          : fallback.aesthetics.motion.pageTransitionEnabled,
        pageTransitionType: themeConfig.aesthetics?.motion?.pageTransitionType || fallback.aesthetics.motion.pageTransitionType,
      },
      depth: {
        shadowProfile: themeConfig.aesthetics?.depth?.shadowProfile || fallback.aesthetics.depth.shadowProfile,
        shadowColor: themeConfig.aesthetics?.depth?.shadowColor || fallback.aesthetics.depth.shadowColor,
      },
      layout: {
        uiDensity: themeConfig.aesthetics?.layout?.uiDensity || fallback.aesthetics.layout.uiDensity,
        contentMaxWidth: themeConfig.aesthetics?.layout?.contentMaxWidth || fallback.aesthetics.layout.contentMaxWidth,
      },
    },
    table: {
      headerPadding: themeConfig.table?.headerPadding || fallback.table.headerPadding,
      cellPadding: themeConfig.table?.cellPadding || fallback.table.cellPadding,
      headerFontSize: themeConfig.table?.headerFontSize || fallback.table.headerFontSize,
      headerFontWeight: themeConfig.table?.headerFontWeight || fallback.table.headerFontWeight,
      headerUppercase: typeof themeConfig.table?.headerUppercase === 'boolean'
        ? themeConfig.table.headerUppercase
        : fallback.table.headerUppercase,
      headerLetterSpacing: themeConfig.table?.headerLetterSpacing || fallback.table.headerLetterSpacing,
      cellVerticalAlign: themeConfig.table?.cellVerticalAlign || fallback.table.cellVerticalAlign,
      stripeStyle: themeConfig.table?.stripeStyle || fallback.table.stripeStyle,
      borderStyle: themeConfig.table?.borderStyle || fallback.table.borderStyle,
      headerVariant: themeConfig.table?.headerVariant || fallback.table.headerVariant,
      rowSpacing: themeConfig.table?.rowSpacing || fallback.table.rowSpacing,
      containerStyle: themeConfig.table?.containerStyle || fallback.table.containerStyle,
      tableHeaderWrap: themeConfig.table?.tableHeaderWrap || fallback.table.tableHeaderWrap,
      tableCellWrap: themeConfig.table?.tableCellWrap || fallback.table.tableCellWrap,
      stickyShadow: themeConfig.table?.stickyShadow || fallback.table.stickyShadow,
      collapsibleActions: typeof themeConfig.table?.collapsibleActions === 'boolean'
        ? themeConfig.table.collapsibleActions
        : fallback.table.collapsibleActions,
      tableRowContrast: typeof themeConfig.table?.tableRowContrast === 'boolean'
        ? themeConfig.table.tableRowContrast
        : fallback.table.tableRowContrast,
      tableRowHoverContrast: typeof themeConfig.table?.tableRowHoverContrast === 'boolean'
        ? themeConfig.table.tableRowHoverContrast
        : fallback.table.tableRowHoverContrast,
      tableTextSize: themeConfig.table?.tableTextSize || fallback.table.tableTextSize,
    },
    tokenMappings: {
      padding: { ...fallback.tokenMappings.padding, ...themeConfig.tokenMappings?.padding },
      fontSize: { ...fallback.tokenMappings.fontSize, ...themeConfig.tokenMappings?.fontSize },
      fontWeight: { ...fallback.tokenMappings.fontWeight, ...themeConfig.tokenMappings?.fontWeight },
      letterSpacing: { ...fallback.tokenMappings.letterSpacing, ...themeConfig.tokenMappings?.letterSpacing },
      statusClasses: { ...fallback.tokenMappings.statusClasses, ...themeConfig.tokenMappings?.statusClasses },
    },
  };
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { resolvedTheme, toggleTheme: toggleDarkModeStore } = useThemeStore();
  const [theme, setTheme] = useState<ThemeConfiguration>(() => {
    return validateThemeStructure(ConfigurationService.getThemeConfig());
  });

  const isDark = resolvedTheme === 'dark';

  /**
   * Helper to convert hex color to space-separated RGB components.
   */
  const hexToRgb = (hex: string): string => {
    const cleanHex = hex.replace('#', '');
    const normalizedHex = cleanHex.length === 3
      ? cleanHex.split('').map(char => char + char).join('')
      : cleanHex;

    const r = parseInt(normalizedHex.substring(0, 2), 16);
    const g = parseInt(normalizedHex.substring(2, 4), 16);
    const b = parseInt(normalizedHex.substring(4, 6), 16);

    return isNaN(r) || isNaN(g) || isNaN(b) ? '0 0 0' : `${r} ${g} ${b}`;
  };

  /**
   * Loads theme configuration from ConfigurationService.
   */
  const loadTheme = useCallback(() => {
    try {
      const themeConfig = ConfigurationService.getThemeConfig();
      setTheme(validateThemeStructure(themeConfig));
    } catch (error) {
      console.error('Failed to load theme configuration:', error);
    }
  }, []);

  /**
   * Derived table configuration that merges global and mode-specific settings.
   */
  const resolvedTable = useMemo<TableConfiguration>(() => {
    if (!theme) {
      // Return hardcoded safety defaults if theme is not yet loaded
      return {
        headerPadding: '1rem 1.5rem',
        cellPadding: '1rem 1.5rem',
        headerFontSize: '0.75rem',
        headerFontWeight: '600',
        headerUppercase: true,
        headerLetterSpacing: '0.05em',
        cellVerticalAlign: 'middle',
        stripeStyle: 'subtle',
        borderStyle: 'horizontal',
        headerVariant: 'filled',
        rowSpacing: 'none',
        containerStyle: 'card',
        tableHeaderWrap: 'nowrap',
        tableCellWrap: 'wrap',
        stickyShadow: 'none',
        collapsibleActions: false,
        tableRowContrast: false,
        tableRowHoverContrast: false,
        tableTextSize: 'MD'
      };
    }

    const modeColors = isDark ? theme.darkMode : theme.lightMode;
    const modeTable = (modeColors.table || {}) as Partial<TableConfiguration>;
    const globalTable = (theme.table || {}) as Partial<TableConfiguration>;

    return {
      headerPadding: modeTable.headerPadding || globalTable.headerPadding || '1rem 1.5rem',
      cellPadding: modeTable.cellPadding || globalTable.cellPadding || '1rem 1.5rem',
      headerFontSize: modeTable.headerFontSize || globalTable.headerFontSize || '0.75rem',
      headerFontWeight: modeTable.headerFontWeight || globalTable.headerFontWeight || '600',
      headerUppercase: modeTable.headerUppercase ?? globalTable.headerUppercase ?? true,
      headerLetterSpacing: modeTable.headerLetterSpacing || globalTable.headerLetterSpacing || '0.05em',
      cellVerticalAlign: modeTable.cellVerticalAlign || globalTable.cellVerticalAlign || 'middle',
      stripeStyle: modeTable.stripeStyle || globalTable.stripeStyle || 'subtle',
      borderStyle: modeTable.borderStyle || globalTable.borderStyle || 'horizontal',
      headerVariant: modeTable.headerVariant || globalTable.headerVariant || 'filled',
      rowSpacing: modeTable.rowSpacing || globalTable.rowSpacing || 'none',
      containerStyle: modeTable.containerStyle || globalTable.containerStyle || 'card',
      tableHeaderWrap: modeTable.tableHeaderWrap || globalTable.tableHeaderWrap || 'nowrap',
      tableCellWrap: modeTable.tableCellWrap || globalTable.tableCellWrap || 'wrap',
      stickyShadow: modeTable.stickyShadow || globalTable.stickyShadow || 'none',
      collapsibleActions: modeTable.collapsibleActions ?? globalTable.collapsibleActions ?? false,
      tableRowContrast: modeTable.tableRowContrast ?? globalTable.tableRowContrast ?? false,
      tableRowHoverContrast: modeTable.tableRowHoverContrast ?? globalTable.tableRowHoverContrast ?? false,
      tableTextSize: modeTable.tableTextSize || globalTable.tableTextSize || 'MD'
    };
  }, [theme, isDark]);

  /**
   * Derived token mappings that merge global and mode-specific overrides.
   */
  const resolvedMappings = useMemo<TokenMappings>(() => {
    const defaultMappings: TokenMappings = {
      padding: {}, fontSize: {}, fontWeight: {}, letterSpacing: {}, statusClasses: {}
    };

    if (!theme) return defaultMappings;

    const baseMappings = (theme.tokenMappings || {}) as Partial<TokenMappings>;
    const modeColors = isDark ? theme.darkMode : theme.lightMode;
    const modeMappings = (modeColors.tokenMappings || {}) as Partial<TokenMappings>;

    return {
      padding: { ...(baseMappings.padding || {}), ...(modeMappings.padding || {}) },
      fontSize: { ...(baseMappings.fontSize || {}), ...(modeMappings.fontSize || {}) },
      fontWeight: { ...(baseMappings.fontWeight || {}), ...(modeMappings.fontWeight || {}) },
      letterSpacing: { ...(baseMappings.letterSpacing || {}), ...(modeMappings.letterSpacing || {}) },
      statusClasses: { ...(baseMappings.statusClasses || {}), ...(modeMappings.statusClasses || {}) },
    };
  }, [theme, isDark]);

  /**
   * Applies theme configuration as CSS variables to document root.
   */
  const applyThemeVariables = useCallback((
    themeConfig: ThemeConfiguration,
    table: TableConfiguration,
    mappings: TokenMappings
  ) => {
    const root = document.documentElement;

    // Apply color variables
    root.style.setProperty('--color-primary', themeConfig.primaryColor);
    root.style.setProperty('--color-primary-rgb', hexToRgb(themeConfig.primaryColor));
    root.style.setProperty('--color-secondary', themeConfig.secondaryColor);
    root.style.setProperty('--color-secondary-rgb', hexToRgb(themeConfig.secondaryColor));
    root.style.setProperty('--color-accent', themeConfig.accentColor);

    // Apply status colors
    root.style.setProperty('--color-success', themeConfig.statusColors?.success || '#22c55e');
    root.style.setProperty('--color-warning', themeConfig.statusColors?.warning || '#eab308');
    root.style.setProperty('--color-danger', themeConfig.statusColors?.error || '#ef4444');
    root.style.setProperty('--color-neutral', themeConfig.statusColors?.neutral || '#94a3b8');
    root.style.setProperty('--color-info', themeConfig.statusColors?.info || '#3b82f6');

    // Inject Mode-specific colors
    const modeColors = isDark ? themeConfig.darkMode : themeConfig.lightMode;

    root.style.setProperty('--color-bg-primary', modeColors.bgPrimary);
    root.style.setProperty('--color-bg-primary-rgb', hexToRgb(modeColors.bgPrimary));
    root.style.setProperty('--color-bg-secondary', modeColors.bgSecondary);
    root.style.setProperty('--color-bg-secondary-rgb', hexToRgb(modeColors.bgSecondary));
    root.style.setProperty('--color-bg-surface', modeColors.bgSurface);
    root.style.setProperty('--color-bg-surface-rgb', hexToRgb(modeColors.bgSurface));
    root.style.setProperty('--color-text-primary', modeColors.textPrimary);
    root.style.setProperty('--color-text-secondary', modeColors.textSecondary);
    root.style.setProperty('--color-divider', modeColors.divider);
    root.style.setProperty('--color-divider-rgb', hexToRgb(modeColors.divider));
    root.style.setProperty('--color-divider-width', modeColors.dividerWidth || '1px');
    root.style.setProperty('--color-divider-opacity', (modeColors.dividerOpacity ?? 1).toString());
    root.style.setProperty('--color-partition', modeColors.partition);
    root.style.setProperty('--color-partition-rgb', hexToRgb(modeColors.partition));
    root.style.setProperty('--color-selection', modeColors.selection);
    root.style.setProperty('--color-selection-rgb', hexToRgb(modeColors.selection));
    root.style.setProperty('--color-hover-overlay', modeColors.hoverOverlay);
    root.style.setProperty('--color-chart-grid', modeColors.chartGrid);

    // Inject Chart Palette
    modeColors.chartPalette.forEach((color, index) => {
      root.style.setProperty(`--color-chart-${index}`, color);
      root.style.setProperty(`--color-chart-${index}-rgb`, hexToRgb(color));
    });

    // Semantic Accents
    const success_accent = modeColors.successAccent || themeConfig.statusColors?.success || '#22c55e';
    root.style.setProperty('--color-success-surface', modeColors.successSurface || 'rgba(34, 197, 94, 0.1)');
    root.style.setProperty('--color-success-accent', success_accent);
    root.style.setProperty('--color-success-accent-rgb', hexToRgb(success_accent));

    const warning_accent = modeColors.warningAccent || themeConfig.statusColors?.warning || '#eab308';
    root.style.setProperty('--color-warning-surface', modeColors.warningSurface || 'rgba(234, 179, 8, 0.1)');
    root.style.setProperty('--color-warning-accent', warning_accent);
    root.style.setProperty('--color-warning-accent-rgb', hexToRgb(warning_accent));

    const error_accent = modeColors.errorAccent || themeConfig.statusColors?.error || '#ef4444';
    root.style.setProperty('--color-error-surface', modeColors.errorSurface || 'rgba(239, 68, 68, 0.1)');
    root.style.setProperty('--color-error-accent', error_accent);
    root.style.setProperty('--color-error-accent-rgb', hexToRgb(error_accent));

    const info_accent = modeColors.infoAccent || themeConfig.statusColors?.info || '#3b82f6';
    root.style.setProperty('--color-info-surface', modeColors.infoSurface || 'rgba(59, 130, 246, 0.1)');
    root.style.setProperty('--color-info-accent', info_accent);
    root.style.setProperty('--color-info-accent-rgb', hexToRgb(info_accent));

    // Status Badge & Transition High-Fidelity Injection (100% Dynamic)
    // These variables enable institutional control over badge vibrancy and transition vector clarity.
    root.style.setProperty('--status-badge-mix', modeColors.statusBadgeMix || (isDark ? 'white 30%' : 'black 45%'));
    root.style.setProperty('--status-badge-opacity', (modeColors.statusBadgeOpacity ?? (isDark ? 0.25 : 0.1)).toString());
    root.style.setProperty('--transition-arrow-opacity', (modeColors.transitionArrowOpacity ?? (isDark ? 1.0 : 0.5)).toString());
    root.style.setProperty('--transition-arrow-weight', modeColors.transitionArrowWeight || (isDark ? '700' : '400'));

    root.style.setProperty('--sidebar-text', modeColors.sidebarText);
    root.style.setProperty('--sidebar-text-rgb', hexToRgb(modeColors.sidebarText));
    root.style.setProperty('--sidebar-icon', modeColors.sidebarIcon);
    root.style.setProperty('--sidebar-icon-rgb', hexToRgb(modeColors.sidebarIcon));
    root.style.setProperty('--color-card-bg', modeColors.cardBg);
    root.style.setProperty('--color-stats-container-bg', modeColors.statsContainerBg);
    root.style.setProperty('--color-stats-container-bg-rgb', hexToRgb(modeColors.statsContainerBg));
    root.style.setProperty('--color-stats-container-bg-opacity', (modeColors.statsContainerOpacity ?? 1).toString());
    root.style.setProperty('--color-dashboard-card-bg', modeColors.dashboardCardBg);
    root.style.setProperty('--color-dashboard-card-bg-rgb', hexToRgb(modeColors.dashboardCardBg));
    root.style.setProperty('--color-dashboard-card-bg-opacity', (modeColors.dashboardCardOpacity ?? 1).toString());

    const tableBg = modeColors.tableBg || modeColors.bgSurface;
    root.style.setProperty('--color-table-bg', tableBg);
    root.style.setProperty('--color-table-bg-rgb', hexToRgb(tableBg));
    root.style.setProperty('--color-table-bg-opacity', (modeColors.tableBgOpacity ?? 1).toString());

    const tableHeaderBg = modeColors.tableHeaderBg || modeColors.bgSecondary;
    root.style.setProperty('--color-table-header-bg', tableHeaderBg);
    root.style.setProperty('--color-table-header-bg-rgb', hexToRgb(tableHeaderBg));
    root.style.setProperty('--color-table-header-bg-opacity', (modeColors.tableHeaderBgOpacity ?? 1).toString());

    root.style.setProperty('--color-table-header-color', modeColors.tableHeaderColor || modeColors.textPrimary);
    root.style.setProperty('--color-header-text', modeColors.headerText || modeColors.textPrimary);

    const tableBorder = modeColors.tableBorder || modeColors.divider;
    root.style.setProperty('--color-table-border', tableBorder);
    root.style.setProperty('--color-table-border-rgb', hexToRgb(tableBorder));
    root.style.setProperty('--color-table-border-opacity', (modeColors.tableBorderOpacity ?? 1).toString());

    // State-Specific Table Elements (Stripe/Hover/Selected)
    // Stripe 1 = Odd Row (Applicable in Dual mode)
    const stripeColor = modeColors.tableStripeColor1 || (isDark ? '#ffffff' : '#000000');
    const stripeOpacity = modeColors.tableStripeOpacity1 ?? 0.02;
    root.style.setProperty('--color-table-stripe-1', stripeColor);
    root.style.setProperty('--color-table-stripe-1-rgb', hexToRgb(stripeColor));
    root.style.setProperty('--color-table-stripe-1-opacity', stripeOpacity.toString());

    // Stripe 2 = Even Row (Standard Zebra Stripe)
    const stripeColor2 = modeColors.tableStripeColor2 || (isDark ? '#f8fafc' : '#111827');
    const stripeOpacity2 = modeColors.tableStripeOpacity2 ?? 0.035;
    root.style.setProperty('--color-table-stripe-2', stripeColor2);
    root.style.setProperty('--color-table-stripe-2-rgb', hexToRgb(stripeColor2));
    root.style.setProperty('--color-table-stripe-2-opacity', stripeOpacity2.toString());

    const hoverColor = modeColors.tableHoverColor || (isDark ? '#ffffff' : '#000000');
    const hoverOpacity = modeColors.tableHoverOpacity ?? 0.05;
    root.style.setProperty('--color-table-hover', hoverColor);
    root.style.setProperty('--color-table-hover-rgb', hexToRgb(hoverColor));
    root.style.setProperty('--color-table-hover-opacity', hoverOpacity.toString());

    // Row Contrast & Text Settings
    root.style.setProperty('--table-row-contrast', table.tableRowContrast ? '1' : '0');
    root.style.setProperty('--table-row-hover-contrast', table.tableRowHoverContrast ? '1' : '0');
    root.style.setProperty('--table-text-size', mapTokenToCss(table.tableTextSize, mappings) || '0.875rem');

    const selColor = modeColors.tableSelectedColor || themeConfig.primaryColor;
    const selOpacity = modeColors.tableSelectedOpacity ?? 0.1;
    root.style.setProperty('--color-table-selected', selColor);
    root.style.setProperty('--color-table-selected-rgb', hexToRgb(selColor));
    root.style.setProperty('--color-table-selected-opacity', selOpacity.toString());

    root.style.setProperty('--color-table-sticky-bg', tableBg);
    root.style.setProperty('--color-table-sticky-border', tableBorder);

    // Apply Elevation
    root.style.setProperty('--rim-light-opacity', modeColors.elevation.rimLightOpacity.toString());
    root.style.setProperty('--shadow-rim-color', modeColors.elevation.shadowColor || (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'));
    root.style.setProperty('--border-radius', themeConfig.borderRadius);

    // Table Granular Injection
    root.style.setProperty('--table-header-padding', mapTokenToCss(table.headerPadding, mappings));
    root.style.setProperty('--table-cell-padding', mapTokenToCss(table.cellPadding, mappings));
    root.style.setProperty('--table-header-size', mapTokenToCss(table.headerFontSize, mappings));
    root.style.setProperty('--table-header-weight', mapTokenToCss(table.headerFontWeight, mappings));
    root.style.setProperty('--table-header-transform', table.headerUppercase ? 'uppercase' : 'none');
    root.style.setProperty('--table-header-spacing', mapTokenToCss(table.headerLetterSpacing, mappings));
    root.style.setProperty('--table-cell-align', table.cellVerticalAlign);
    root.style.setProperty('--table-stripe-style', table.stripeStyle);
    root.style.setProperty('--table-border-style', table.borderStyle);
    root.style.setProperty('--table-header-variant', table.headerVariant);
    root.style.setProperty('--table-row-spacing', table.rowSpacing);

    // Map Wrapping Enum keys to CSS values
    const wrapMapping: Record<string, string> = { 'wrap': 'normal', 'nowrap': 'nowrap' };
    root.style.setProperty('--table-header-wrap', wrapMapping[table.tableHeaderWrap] || 'nowrap');
    root.style.setProperty('--table-cell-wrap', wrapMapping[table.tableCellWrap] || 'normal');

    // Sticky Shadow Profiles
    const shadowMapping: Record<string, { left: string; right: string }> = {
      'none': { left: 'none', right: 'none' },
      'soft': {
        left: '4px 0 8px -4px rgba(0, 0, 0, 0.1)',
        right: '-4px 0 8px -4px rgba(0, 0, 0, 0.1)'
      },
      'moderate': {
        left: '8px 0 12px -4px rgba(0, 0, 0, 0.15)',
        right: '-8px 0 12px -4px rgba(0, 0, 0, 0.15)'
      },
      'sharp': {
        left: '1px 0 0 0 rgba(0, 0, 0, 0.2)',
        right: '-1px 0 0 0 rgba(0, 0, 0, 0.2)'
      }
    };
    const shadows = shadowMapping[table.stickyShadow] || shadowMapping['none'];
    root.style.setProperty('--table-sticky-shadow-left', shadows.left);
    root.style.setProperty('--table-sticky-shadow-right', shadows.right);

    // Typography Engine - 100% Dynamic & Configured
    const typography = themeConfig.typography;

    // Map single config font to both Primary and Secondary tokens for Enterprise System compatibility
    root.style.setProperty('--font-family-primary', typography.fontFamily);
    root.style.setProperty('--font-family-secondary', typography.fontFamily);
    root.style.setProperty('--font-family-main', typography.fontFamily); // Legacy support

    // Dynamic Font Sizes (Base scales) - Anchored by Admin Panel Configuration
    const basePx = parseFloat(typography.fontSize.base) || TYPOGRAPHY_DEFAULTS.BASE_PX;
    const smPx = parseFloat(typography.fontSize.sm) || TYPOGRAPHY_DEFAULTS.SM_PX;
    const lgPx = parseFloat(typography.fontSize.lg) || TYPOGRAPHY_DEFAULTS.LG_PX;

    // Computed Scale Injection - Eliminates Magic Numbers
    root.style.setProperty('--font-size-xs', `${Math.round(smPx * TYPOGRAPHIC_SCALE.XS_FACTOR)}px`);
    root.style.setProperty('--font-size-sm', `${smPx}px`);
    root.style.setProperty('--font-size-base', `${basePx}px`);
    root.style.setProperty('--font-size-lg', `${lgPx}px`); // Maps to tailwind text-lg
    root.style.setProperty('--font-size-xl', `${Math.round(lgPx * TYPOGRAPHIC_SCALE.XL_FACTOR)}px`);
    root.style.setProperty('--font-size-2xl', `${Math.round(lgPx * TYPOGRAPHIC_SCALE['2XL_FACTOR'])}px`);
    root.style.setProperty('--font-size-3xl', `${Math.round(lgPx * TYPOGRAPHIC_SCALE['3XL_FACTOR'])}px`);
    root.style.setProperty('--font-size-4xl', `${Math.round(lgPx * TYPOGRAPHIC_SCALE['4XL_FACTOR'])}px`);
    root.style.setProperty('--font-size-5xl', `${Math.round(lgPx * TYPOGRAPHIC_SCALE['5XL_FACTOR'])}px`);

    // Dynamic Line Heights
    root.style.setProperty('--line-height-base', typography.lineHeight);

    // Dynamic Tracking
    root.style.setProperty('--font-tracking-tight', typography.tracking.tight);
    root.style.setProperty('--font-tracking-normal', typography.tracking.normal);
    root.style.setProperty('--font-tracking-wide', typography.tracking.wide);

    // Font Features
    root.style.setProperty('--font-feature-settings', themeConfig.typography.fontFeatureSettings || "'cv02', 'cv03', 'tabular-nums'");

    // Dynamic Font Weights from Token Mappings
    // This allows the Admin Config to override semantic weights (e.g. 'bold' -> 600 instead of 700)
    if (mappings.fontWeight) {
      Object.entries(mappings.fontWeight).forEach(([key, value]) => {
        root.style.setProperty(`--font-weight-${key}`, value);
      });
    }

    // Dynamic Google Fonts Loader
    const fontId = 'dynamic-theme-font';
    let fontLink = document.getElementById(fontId) as HTMLLinkElement;
    if (!fontLink) {
      fontLink = document.createElement('link');
      fontLink.id = fontId;
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
    const fontParam = (typography.fontFamily || 'Inter').replace(/\s+/g, '+');
    fontLink.href = `https://fonts.googleapis.com/css2?family=${fontParam}:wght@300;400;500;600;700;800;900&display=swap`;

    // Aesthetics
    const aesthetics = modeColors.aesthetics || themeConfig.aesthetics;

    // 1. Glassmorphism Engine
    root.style.setProperty('--glass-enabled', aesthetics.glassmorphism.enabled ? '1' : '0');
    root.style.setProperty('--glass-blur', aesthetics.glassmorphism.blurIntensity);
    root.style.setProperty('--glass-opacity', aesthetics.glassmorphism.transparency.toString());
    root.style.setProperty('--glass-noise', (aesthetics.glassmorphism.noiseAlpha || 0.03).toString());
    root.style.setProperty('--glass-saturation', (aesthetics.glassmorphism.saturation || 1.2).toString());

    // 2. Orchestrated Motion
    const speedMap: Record<string, string> = {
      instant: '0ms',
      quick: '150ms',
      standard: '300ms',
      smooth: '600ms'
    };
    root.style.setProperty('--ui-animation-speed', speedMap[aesthetics.motion.speed] || '300ms');

    const easingMap: Record<string, string> = {
      productive: 'cubic-bezier(0.2, 0, 0, 1)',
      expressive: 'cubic-bezier(0.4, 0, 0.2, 1)',
      emphasized: 'cubic-bezier(0.83, 0, 0.17, 1)'
    };
    root.style.setProperty('--motion-easing', easingMap[aesthetics.motion.easingCurve] || 'cubic-bezier(0.4, 0, 0.2, 1)');
    root.style.setProperty('--motion-stagger', `${aesthetics.motion.staggerDelayMs || 30}ms`);
    root.style.setProperty('--interactive-scale', aesthetics.motion.interactiveScale ? '1.05' : '1.0');

    // Page Transition Setup
    const pageTransitionEnabled = aesthetics.motion.pageTransitionEnabled !== false;
    const pageTransitionType = aesthetics.motion.pageTransitionType || 'fade-slide';
    root.style.setProperty('--page-transition-enabled', pageTransitionEnabled ? '1' : '0');
    root.setAttribute('data-page-transition-enabled', pageTransitionEnabled ? 'true' : 'false');
    if (pageTransitionEnabled) {
      root.setAttribute('data-page-transition-type', pageTransitionType);
    } else {
      root.removeAttribute('data-page-transition-type');
    }

    // 3. Depth & Dimension Profiles
    const shadowColorHex = aesthetics.depth.shadowColor || '#000000';
    root.style.setProperty('--shadow-color-rgb', hexToRgb(shadowColorHex));

    const shadowMap: Record<string, string> = {
      none: 'none',
      soft: isDark
        ? '0 4px 12px rgba(var(--shadow-color-rgb), 0.4)'
        : '0 4px 12px rgba(var(--shadow-color-rgb), 0.05)',
      sharp: isDark
        ? '4px 4px 0px rgba(var(--shadow-color-rgb), 0.5)'
        : '4px 4px 0px rgba(var(--shadow-color-rgb), 0.1)',
      deep: isDark
        ? '0 20px 40px rgba(var(--shadow-color-rgb), 0.6)'
        : '0 20px 40px rgba(var(--shadow-color-rgb), 0.15)'
    };
    root.style.setProperty('--component-shadow', shadowMap[aesthetics.depth.shadowProfile] || 'none');

    // 4. Layout Density & Boundary
    const spacingMap: Record<string, string> = {
      compact: '0.8',
      default: '1',
      spacious: '1.2'
    };
    root.style.setProperty('--ui-spacing-multiplier', spacingMap[aesthetics.layout.uiDensity] || '1');
    root.style.setProperty('--max-width-content', aesthetics.layout.contentMaxWidth);

    // Sync HTML class
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleDarkMode = useCallback(() => {
    toggleDarkModeStore();
  }, [toggleDarkModeStore]);

  const refreshTheme = useCallback(async () => {
    loadTheme();
  }, [loadTheme]);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  useLayoutEffect(() => {
    applyThemeVariables(theme, resolvedTable, resolvedMappings);
  }, [theme, resolvedTable, resolvedMappings, applyThemeVariables]);

  useEffect(() => {
    const handleUpdate = (e: CustomEvent) => {
      const { key } = e.detail;
      if (key === UPDATE_KEYS.THEME || key === UPDATE_KEYS.ALL) {
        refreshTheme();
      }
    };
    window.addEventListener(CONFIG_EVENTS.UPDATED as string, handleUpdate as EventListener);
    return () => window.removeEventListener(CONFIG_EVENTS.UPDATED as string, handleUpdate as EventListener);
  }, [refreshTheme]);

  const value: ThemeContextValue = {
    theme,
    isDark,
    toggleDarkMode,
    refreshTheme,
    tokenMappings: resolvedMappings,
    resolvedTable
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/**
 * Custom hook to access theme context.
 * Must be used within ThemeProvider.
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
